import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {User, Question, HubblFilter} from 'hubbl-shared';
import {
  fetchUserDetails,
  updateUserLanguage,
  initializeUserLanguage,
  updateUserProfilePhoto,
  updateUserInterests,
  updateUserBasicInfo,
} from './UserThunks';

interface UserState {
  userDetails: User | null;
  eventCreation: {
    newQuestions: Question[];
    newEvaluationQuestions: Question[];
    visibilityFilters: HubblFilter[];
  };
}

const initialState: UserState = {
  userDetails: null,
  eventCreation: {
    newQuestions: [],
    newEvaluationQuestions: [],
    visibilityFilters: [],
  },
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // Reset user details
    resetUserDetails: state => {
      state.userDetails = null;
      state.eventCreation.visibilityFilters = [];
    },

    // Add visibility filter to the event
    addEventVisibilityFilters: (
      state,
      action: PayloadAction<HubblFilter[]>,
    ) => {
      state.eventCreation.visibilityFilters = action.payload;
    },

    // Add a question to the event
    addEventQuestion: (state, action: PayloadAction<Question>) => {
      state.eventCreation.newQuestions.push(action.payload);
    },
    resetEventQuestions: state => {
      state.eventCreation.newQuestions = [];
    },

    // Remove a question from the event by id
    removeEventQuestion: (state, action: PayloadAction<string>) => {
      state.eventCreation.newQuestions =
        state.eventCreation.newQuestions.filter(
          question => question.id !== action.payload,
        );
    },

    // Update a specific question (e.g., edit question text or options)
    updateEventQuestion: (state, action: PayloadAction<Question>) => {
      const index = state.eventCreation.newQuestions.findIndex(
        question => question.id === action.payload.id,
      );
      if (index !== -1) {
        state.eventCreation.newQuestions[index] = action.payload;
      }
    },

    // Reorder questions using indices
    reorderQuestions: (
      state,
      action: PayloadAction<{sourceIndex: number; destinationIndex: number}>,
    ) => {
      const {sourceIndex, destinationIndex} = action.payload;
      const [movedQuestion] = state.eventCreation.newQuestions.splice(
        sourceIndex,
        1,
      );
      state.eventCreation.newQuestions.splice(
        destinationIndex,
        0,
        movedQuestion,
      );
    },

    // Add an evaluation question to the event
    addEvaluationQuestion: (state, action: PayloadAction<Question>) => {
      state.eventCreation.newEvaluationQuestions.push(action.payload);
    },
    resetEvaluationQuestions: state => {
      state.eventCreation.newEvaluationQuestions = [];
    },
    removeEvaluationQuestion: (state, action: PayloadAction<string>) => {
      state.eventCreation.newEvaluationQuestions =
        state.eventCreation.newEvaluationQuestions.filter(
          question => question.id !== action.payload,
        );
    },
    updateEvaluationQuestion: (state, action: PayloadAction<Question>) => {
      const index = state.eventCreation.newEvaluationQuestions.findIndex(
        question => question.id === action.payload.id,
      );
      if (index !== -1) {
        state.eventCreation.newEvaluationQuestions[index] = action.payload;
      }
    },
    reorderEvaluationQuestions: (
      state,
      action: PayloadAction<{sourceIndex: number; destinationIndex: number}>,
    ) => {
      const {sourceIndex, destinationIndex} = action.payload;
      const [movedQuestion] = state.eventCreation.newEvaluationQuestions.splice(
        sourceIndex,
        1,
      );
      state.eventCreation.newEvaluationQuestions.splice(
        destinationIndex,
        0,
        movedQuestion,
      );
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchUserDetails.fulfilled, (state, action) => {
      state.userDetails = action.payload;
    });
    builder.addCase(updateUserLanguage.fulfilled, (state, action) => {
      if (state.userDetails) {
        state.userDetails.settings = {
          ...state.userDetails.settings,
          preferredLanguage: action.payload,
        };
      }
    });
    builder.addCase(initializeUserLanguage.fulfilled, (state, action) => {
      // Language initialization is handled in the thunk
      // This case is just for completeness
    });
    builder.addCase(updateUserProfilePhoto.fulfilled, (state, action) => {
      if (state.userDetails) {
        state.userDetails.photoUrl = action.payload;
      }
    });
    builder.addCase(updateUserInterests.fulfilled, (state, action) => {
      if (state.userDetails) {
        state.userDetails.interests = action.payload;
      }
    });
    builder.addCase(updateUserBasicInfo.fulfilled, (state, action) => {
      if (state.userDetails) {
        state.userDetails.fullName = action.payload.fullName;
        state.userDetails.firstName = action.payload.firstName;
        state.userDetails.jobTitle = action.payload.jobTitle;
        state.userDetails.department = action.payload.department;
      }
    });
  },
});

export const {
  resetUserDetails,
  addEventQuestion,
  reorderQuestions,
  addEventVisibilityFilters,
  removeEventQuestion,
  updateEventQuestion,
  resetEventQuestions,
  addEvaluationQuestion,
  resetEvaluationQuestions,
  removeEvaluationQuestion,
  updateEvaluationQuestion,
  reorderEvaluationQuestions,
} = userSlice.actions;

export const UserReducer = userSlice.reducer;
